import React, { useState, useCallback, useRef } from 'react';
import { Image } from '../Image/Image';
import { VideoModal } from '../VideoModal/VideoModal';
import { VideoButton, type VideoButtonSize } from '../VideoButton/VideoButton';
import { getYouTubeEmbedUrl } from '../../utils/youtube';
import './Video.scss';

/**
 * Video - Reusable video thumbnail with play button overlay
 *
 * Displays a poster image with a centered play icon. On hover, the image
 * scales slightly and a dark overlay appears. Supports three play behaviors:
 * - `'navigate'`: Clicking navigates to a video page URL
 * - `'lightbox'`: Clicking opens a modal with an embedded video player
 * - `'inline'`: Clicking replaces the poster with the player in place (no modal)
 *
 * Designed to be consumed standalone, inside FeaturedCards interactive cards,
 * in MegaMenu featured panels, or any component needing video thumbnails.
 */

export type VideoPlayBehavior = 'navigate' | 'lightbox' | 'inline';

export interface VideoProps {
  /** Poster/thumbnail image URL */
  posterUrl: string;

  /** Poster image alt text */
  posterAlt?: string;

  /** Video URL (YouTube, MP4, or page URL depending on playBehavior) */
  videoUrl: string;

  /** Play behavior: 'navigate' links to URL, 'lightbox' opens modal player, 'inline' plays in place (no modal) */
  playBehavior?: VideoPlayBehavior;

  /** Optional duration label (e.g. "5:36") */
  duration?: string;

  /** Optional eyebrow label (e.g. "Video") */
  label?: string;

  /** Optional aspect ratio override (default: 2/1) */
  aspectRatio?: string;

  /** Optional className for the container */
  className?: string;

  /**
   * Visual size of the play-circle button overlay.
   * - `'sm'` — 48px (compact thumbnails)
   * - `'md'` — 72px (default; featured cards, MegaMenu)
   * - `'lg'` — 90px (hero / full-bleed)
   */
  playButtonSize?: VideoButtonSize;

  /** Callback when play is triggered */
  onPlay?: () => void;

  /** Callback when the lightbox closes (close button, backdrop, or Escape) */
  onClose?: () => void;
}

export const Video: React.FC<VideoProps> = ({
  posterUrl,
  posterAlt = '',
  videoUrl,
  playBehavior = 'lightbox',
  duration,
  label,
  aspectRatio,
  className,
  playButtonSize = 'md',
  onPlay,
  onClose,
}) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isInlinePlaying, setIsInlinePlaying] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handlePlay = useCallback(() => {
    onPlay?.();
    if (playBehavior === 'lightbox') {
      setIsLightboxOpen(true);
    } else if (playBehavior === 'inline') {
      setIsInlinePlaying(true);
    }
    // 'navigate' is handled by the <a> tag
  }, [playBehavior, onPlay]);

  const handleClose = useCallback(() => {
    setIsLightboxOpen(false);
    onClose?.();
  }, [onClose]);

  const embedUrl = getYouTubeEmbedUrl(videoUrl);
  const classes = ['video', className].filter(Boolean).join(' ');

  // Poster stays mounted behind the player, which fades in once loaded, so the
  // section never blanks while the iframe/video fetches. YouTube renders its own
  // controls; direct files use native controls.
  if (playBehavior === 'inline' && isInlinePlaying) {
    const playerClass = `video__inline-player${playerReady ? ' video__inline-player--ready' : ''}`;
    return (
      <div className={classes}>
        <div className="video__inline" style={{ aspectRatio: aspectRatio || '16 / 9' }}>
          <Image url={posterUrl} alt="" objectFit="cover" className="video__inline-poster" />
          {embedUrl ? (
            <iframe
              className={playerClass}
              src={embedUrl}
              title={posterAlt || 'Video player'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onLoad={() => setPlayerReady(true)}
            />
          ) : (
            <video
              className={playerClass}
              src={videoUrl}
              poster={posterUrl}
              controls
              autoPlay
              onLoadedData={() => setPlayerReady(true)}
            >
              <track kind="captions" />
            </video>
          )}
        </div>
      </div>
    );
  }

  const thumbnailContent = (
    <>
      <div className="video__poster" style={aspectRatio ? { aspectRatio } : undefined}>
        <Image url={posterUrl} alt={posterAlt} objectFit="cover" />
        <div className="video__overlay" aria-hidden="true" />
        <VideoButton size={playButtonSize} className="video__play-icon" />
      </div>
      {(label || duration) && (
        <div className="video__meta">
          {label && <span className="video__label">{label}</span>}
          {duration && <span className="video__duration">{duration}</span>}
        </div>
      )}
    </>
  );

  return (
    <>
      {playBehavior === 'navigate' ? (
        <a
          href={videoUrl}
          className={classes}
          aria-label={`Play video: ${posterAlt || 'Video'}`}
          onClick={onPlay}
        >
          {thumbnailContent}
        </a>
      ) : (
        <button
          type="button"
          ref={triggerRef}
          className={classes}
          onClick={handlePlay}
          aria-label={`Play video: ${posterAlt || 'Video'}`}
        >
          {thumbnailContent}
        </button>
      )}

      {playBehavior === 'lightbox' && isLightboxOpen && (
        <VideoModal
          src={videoUrl}
          title={posterAlt || 'Video'}
          onClose={handleClose}
          returnFocusRef={triggerRef}
        />
      )}
    </>
  );
};
